import {
  Browser,
  Device,
  DeviceInfoCustomFlags,
  DeviceInfoDefaults,
  type DeviceInfoInterface,
  Platform,
} from './deviceInfoTypes';
import { generateCustomFlags } from './utils/generateCustomFlags';
import { getBrowser } from './utils/getBrowser';
import { getDevice } from './utils/getDevice';
import { getPlatform } from './utils/getPlatform';

/**
 * DeviceInfo is a singleton that provides information about the device, browser and feature support.
 */
class DeviceInfoClass {
  private initialized: boolean;
  private deviceInfo: DeviceInfoInterface;

  constructor() {
    this.initialized = false;
    this.deviceInfo = DeviceInfoDefaults;
  }

  init(): void {
    // Parse User Agent and generate flags.
    const userAgent = navigator.userAgent || navigator.vendor;
    //Stage 1
    this.deviceInfo.platform = getPlatform(userAgent);
    this.deviceInfo.device = getDevice(userAgent);
    this.deviceInfo.browser = getBrowser(userAgent);

    //Stage 2 - Depends of Stage 1
    // TODO: Add feature checks (touch, multitouch, webgl, webaudio etc.)
    // TODO: Generate deviceInfo flags. E.g. isMobile, isTablet, isDesktop etc.
    this.deviceInfo.customFlags = generateCustomFlags(this.deviceInfo);

    this.initialized = true;
  }

  private initCheck(): boolean {
    if (!this.initialized) {
      console.error('DeviceInfo is not initialized. Please call DeviceInfo.init() first.');
    }

    return this.initialized;
  }

  getPlatform(): Platform {
    this.initCheck();
    return this.deviceInfo.platform;
  }

  getDevice(): Device {
    this.initCheck();
    return this.deviceInfo.device;
  }

  getBrowser(): Browser {
    this.initCheck();
    return this.deviceInfo.browser;
  }

  getCustomFlags(): DeviceInfoCustomFlags {
    this.initCheck();
    return this.deviceInfo.customFlags;
  }

  /**
   * Used in unit tests to reset the singleton instance.
   */
  dispose(): void {
    this.deviceInfo = {} as DeviceInfoInterface;
    this.initialized = false;
  }
}

export const DeviceInfo = new DeviceInfoClass();
